import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";

const isBlank = (value) => !String(value || "").trim();

const textOf = (value) => value || "";

const textLength = (value) => textOf(value).length;

const errorText = (hasError, message) => (hasError ? message : "");

const borderColorFor = (hasError) => (hasError ? "#d32f2f" : "#E8E8E9");

const pick = (value, getFallback) =>
  value === undefined ? getFallback() : value;

const joinIfArray = (value) =>
  Array.isArray(value) ? value.join(", ") : value;

const createLimitedHandler = (onChange, name, max) => (e) => {
  if (e.target.value.length <= max) {
    onChange(name, e.target.value);
  }
};

const resolveScriptCoDetails = (opportunity) => {
  const scriptCoRaw =
    opportunity?.scriptCoDetails || opportunity?.grmDetails || {};
  return (
    scriptCoRaw?.data?.[0] ||
    (Array.isArray(scriptCoRaw?.data)
      ? scriptCoRaw.data[0]
      : scriptCoRaw?.data) ||
    scriptCoRaw?.result?.[0] ||
    scriptCoRaw ||
    {}
  );
};

const resolveDefaultGroupName = (customerDetails, member, opportunity) =>
  customerDetails.patientGroup ||
  customerDetails.PatientGroup ||
  member.groupName ||
  member.groupNum ||
  member.groupNumber ||
  member.group ||
  opportunity?.groupName ||
  opportunity?.groupNum ||
  opportunity?.groupNumber ||
  opportunity?.group ||
  "";

const resolveListValue = (dataValue, member, opportunity, memberKeys, oppKey) =>
  joinIfArray(
    pick(
      dataValue,
      () =>
        memberKeys.map((k) => member[k]).find(Boolean) ||
        opportunity?.[oppKey] ||
        "",
    ),
  );

const isBinNumberInvalid = (binNumber) =>
  isBlank(binNumber) || String(binNumber || "").trim().length < 6;

const ScriptCoPatientDetails = ({
  opportunity,
  data = {},
  onChange,
  onValidationChange,
  showErrors,
}) => {
  const member = opportunity?.member || {};
  const scriptCoDetails = resolveScriptCoDetails(opportunity);
  const customerDetails =
    scriptCoDetails?.customerDetails || scriptCoDetails || {};

  const defaultGroupName = resolveDefaultGroupName(
    customerDetails,
    member,
    opportunity,
  );

  const patientGroup = data.patientGroup || defaultGroupName;
  const insuranceName = pick(data.insuranceName, () =>
    (
      customerDetails.insuranceName ||
      customerDetails.InsuranceName ||
      defaultGroupName ||
      ""
    ).slice(0, 25),
  );
  const binNumber = pick(
    data.binNumber,
    () => customerDetails.binNumber || customerDetails.BinNumber || "010101",
  );
  const scriptcoPatientSourceType = pick(
    data.scriptcoPatientSourceType,
    () =>
      customerDetails.scriptcoPatientSourceType ||
      customerDetails.ScriptcoPatientSourceType ||
      "International",
  );

  const allergies = resolveListValue(
    data.allergies,
    member,
    opportunity,
    ["allergy", "allergies"],
    "allergies",
  );
  const conditions = resolveListValue(
    data.conditions,
    member,
    opportunity,
    ["conditions"],
    "conditions",
  );
  const medications = resolveListValue(
    data.medications,
    member,
    opportunity,
    ["medications"],
    "medications",
  );

  const insuranceError = showErrors && isBlank(insuranceName);
  const binError = showErrors && isBinNumberInvalid(binNumber);
  const allergiesError = showErrors && isBlank(allergies);
  const conditionsError = showErrors && isBlank(conditions);
  const medicationsError = showErrors && isBlank(medications);

  React.useEffect(() => {
    const isInsuranceValid = !isBlank(insuranceName);
    const isBinValid = Boolean(
      binNumber && String(binNumber).trim().length === 6,
    );
    const isAllergiesValid = !isBlank(allergies);
    const isConditionsValid = !isBlank(conditions);
    const isMedicationsValid = !isBlank(medications);

    const isValid =
      isInsuranceValid &&
      isBinValid &&
      isAllergiesValid &&
      isConditionsValid &&
      isMedicationsValid;

    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [
    insuranceName,
    binNumber,
    allergies,
    conditions,
    medications,
    onValidationChange,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;
    onChange(name, finalValue);
  };

  const getBinNumberHelperText = (showErrors, binNumber) => {
    if (!showErrors) return "";

    const trimmed = String(binNumber || "").trim();

    if (!trimmed) return "BIN Number is required";
    if (trimmed.length < 6) return "BIN Number must be 6 digits";
    return "";
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        ScriptCo Patient & Insurance Details
      </Typography>

      {/* Row 1: Patient Group & Insurance Name */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Patient Group (Group Name)
          </Typography>
          <TextField
            name="patientGroup"
            variant="outlined"
            size="small"
            value={patientGroup}
            onChange={(e) => {
              const val = e.target.value.slice(0, 14);
              onChange("patientGroup", val);
            }}
            placeholder="Enter Group Name"
            slotProps={{
              htmlInput: { maxLength: 14 },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#E8E8E9" },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>

        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Insurance Name<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="insuranceName"
            variant="outlined"
            size="small"
            value={insuranceName}
            onChange={(e) => {
              if (e.target.value.length <= 25) {
                handleChange(e);
              }
            }}
            placeholder="Enter Insurance Name"
            slotProps={{
              htmlInput: { maxLength: 25 },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {textLength(insuranceName)}/25
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            error={insuranceError}
            helperText={errorText(insuranceError, "Insurance Name is required")}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: borderColorFor(insuranceError),
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 2: BIN Number & ScriptCo Patient Source Type */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            BIN Number<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="binNumber"
            variant="outlined"
            size="small"
            value={binNumber}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
              onChange("binNumber", val);
            }}
            placeholder="Enter BIN Number"
            slotProps={{
              htmlInput: { maxLength: 6 },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {textLength(binNumber)}/6
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            error={binError}
            helperText={getBinNumberHelperText(showErrors, binNumber)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: borderColorFor(binError),
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>

        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            ScriptCo Patient Source Type
          </Typography>
          <TextField
            name="scriptcoPatientSourceType"
            variant="outlined"
            size="small"
            value={scriptcoPatientSourceType}
            onChange={handleChange}
            placeholder="Enter Source Type"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#E8E8E9" },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 4: Drug Allergies & Current Medical Conditions */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Drug Allergies<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="allergies"
            variant="outlined"
            size="small"
            value={textOf(allergies)}
            onChange={createLimitedHandler(onChange, "allergies", 128)}
            placeholder="Enter allergies"
            error={allergiesError}
            helperText={errorText(allergiesError, "Drug Allergies is required")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {textLength(allergies)}/128
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: borderColorFor(allergiesError),
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>

        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Medical Condition<span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="conditions"
            variant="outlined"
            size="small"
            value={textOf(conditions)}
            onChange={createLimitedHandler(onChange, "conditions", 128)}
            placeholder="Enter medical conditions"
            error={conditionsError}
            helperText={errorText(
              conditionsError,
              "Medical Condition is required",
            )}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {textLength(conditions)}/128
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: borderColorFor(conditionsError),
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
      </Box>

      {/* Row 5: Current Medications and Supplements (Half Width) */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            {"Current Medication"}
            <span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            name="medications"
            variant="outlined"
            size="small"
            value={textOf(medications)}
            onChange={createLimitedHandler(onChange, "medications", 128)}
            placeholder="Enter medications and supplements"
            error={medicationsError}
            helperText={errorText(
              medicationsError,
              "Current Medication is required",
            )}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {textLength(medications)}/128
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: borderColorFor(medicationsError),
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }} />
      </Box>
    </Box>
  );
};

ScriptCoPatientDetails.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default ScriptCoPatientDetails;
